"use client";
import BlokWrap from "@/components/bloks/BlokWrap";
import { useGlobal } from "@/features/global/GlobalContext";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { IS_IOS, IS_SAFARI } from "@/lib/move-to-front/device";
import type { BlokDownloadButton } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";
import { type ComponentProps, useEffect, useState } from "react";
import styles from "./DownloadButton.module.css";

export const DownloadButtonBlok = ({
	blok,
}: BlokProps<BlokDownloadButton> & { color?: string }) => {
	const {
		data: { colorMap },
	} = useGlobal();

	const colorEntry = colorMap[blok.buttonColor!] || "";

	return (
		<BlokWrap editable={blok} as="div">
			<DownloadLink
				blok={blok}
				onClick={() =>
					trackContentClick({
						contentType: blok.component,
						itemId: blok._uid,
						label: blok.text,
					})
				}
				style={
					{
						"--button-bg": colorEntry.color,
						"--button-text": colorEntry.foreground,
					} as React.CSSProperties
				}
				className={styles.button}
			/>
		</BlokWrap>
	);
};

export const DownloadLink = ({
	blok,
	children,
	...props
}: { blok: BlokDownloadButton } & ComponentProps<"a">) => {
	const pdfUrl = blok.pdf?.filename;
	const [url, setUrl] = useState(pdfUrl);
	const pageNumber = blok.pageNumber;

	useEffect(() => {
		if (!pdfUrl || pageNumber === undefined) return;
		try {
			const parsed = new URL(pdfUrl);
			if (!parsed.pathname.endsWith(".pdf")) return;

			if (IS_SAFARI && IS_IOS) {
				parsed.hash = `page${pageNumber}`;
			} else {
				parsed.hash = `page=${pageNumber}`;
			}

			setUrl(parsed.toString());
		} catch (error) {}
	}, [pdfUrl, pageNumber]);

	if (!url) return null;

	return (
		<a href={url} rel="noopener noreferrer" {...props} target="_blank">
			{children || blok.text}
		</a>
	);
};
